import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'diagnostic-fab-position';
const SIZE = { w: 110, h: 26 };

interface Pos { x: number; y: number; }

const getInitial = (): Pos => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return {
    x: Math.max(0, window.innerWidth - SIZE.w - 16),
    y: Math.max(0, window.innerHeight - SIZE.h - 16),
  };
};

const DiagnosticFloatingButton: React.FC = () => {
  const navigate = useNavigate();
  const [pos, setPos] = useState<Pos>(() => getInitial());
  const posRef = useRef<Pos>(pos);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const startRef = useRef<Pos>({ x: 0, y: 0 });
  const offsetRef = useRef<Pos>({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current) return;
      e.preventDefault();
      const nx = e.clientX - offsetRef.current.x;
      const ny = e.clientY - offsetRef.current.y;
      const maxX = Math.max(0, window.innerWidth - SIZE.w);
      const maxY = Math.max(0, window.innerHeight - SIZE.h);
      const clamped = {
        x: Math.min(Math.max(0, nx), maxX),
        y: Math.min(Math.max(0, ny), maxY),
      };
      if (
        Math.abs(e.clientX - startRef.current.x) + Math.abs(e.clientY - startRef.current.y) > 4
      ) {
        movedRef.current = true;
      }
      posRef.current = clamped;
      setPos(clamped);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(posRef.current)); } catch {}
      suppressClickRef.current = movedRef.current;
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    movedRef.current = false;
    suppressClickRef.current = false;
    startRef.current = { x: e.clientX, y: e.clientY };
    offsetRef.current = { x: e.clientX - posRef.current.x, y: e.clientY - posRef.current.y };
  };

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (suppressClickRef.current || movedRef.current) {
      suppressClickRef.current = false;
      movedRef.current = false;
      return;
    }
    navigate('/diagnostico');
  };

  const stopNativeDrag = (e: React.DragEvent | React.MouseEvent) => {
    if ('preventDefault' in e) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const buttonStyle: React.CSSProperties & { WebkitUserDrag?: string } = {
    left: pos.x,
    top: pos.y,
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserDrag: 'none',
  };

  return (
    <button
      type="button"
      onPointerDown={onPointerDown}
      onMouseDown={stopNativeDrag}
      onDragStart={stopNativeDrag}
      draggable={false}
      onClick={onClick}
      style={buttonStyle}
      className="fixed z-[9999] bg-slate-900 text-white text-[10px] px-2 py-1 rounded-md opacity-50 hover:opacity-100 transition-opacity flex items-center gap-1 shadow-lg border border-slate-700 cursor-grab active:cursor-grabbing select-none"
      title="Arraste para mover • Clique para abrir"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
      DIAGNÓSTICO
    </button>
  );
};

export default DiagnosticFloatingButton;
